import { useCallback, useState } from 'react';

import {
  DateRangePreset,
  DateRangeV2,
  UseDateRangePickerV2Options,
  UseDateRangePickerV2Return,
} from '../types';
import { getDateRangeFromPreset, isDateRangePreset } from '../utils';

/**
 * Hook for managing DateRangePickerV2 state
 *
 * Handles conversion between presets and actual date ranges,
 * tracks the selected preset for display purposes.
 *
 * @example
 * ```tsx
 * // Using with a preset default
 * const { dates, selectedPreset, onChange, onPresetSelect } = useDateRangePickerV2({
 *   defaultValue: DateRangePreset.LastMonth
 * });
 *
 * // Using with custom dates default
 * const { dates, selectedPreset, onChange, onPresetSelect } = useDateRangePickerV2({
 *   defaultValue: { startDate: new Date(), endDate: new Date() }
 * });
 *
 * // Unselected state — picker shows placeholder
 * const { dates, selectedPreset, onChange, onPresetSelect } = useDateRangePickerV2({
 *   defaultValue: null
 * });
 * ```
 */
export function useDateRangePickerV2({
  defaultValue = DateRangePreset.ThisWeek,
}: UseDateRangePickerV2Options): UseDateRangePickerV2Return {
  // Lazy initialisers run only on mount — safe to reference defaultValue here
  const [dates, setDates] = useState<DateRangeV2 | null>(() => {
    if (defaultValue == null) return null;
    if (isDateRangePreset(defaultValue)) return getDateRangeFromPreset(defaultValue);
    return defaultValue;
  });

  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset | null>(() => {
    if (defaultValue != null && isDateRangePreset(defaultValue)) return defaultValue;
    return null;
  });

  /**
   * Handles direct date range changes (e.g., from custom calendar selection)
   * Clears the selected preset since it's now a custom range
   */
  const onChange = useCallback(
    (newDates: DateRangeV2) => {
      setDates(newDates);
      setSelectedPreset(null);
    },
    [setDates, setSelectedPreset]
  );

  /**
   * Handles preset selection
   * Converts preset to date range and updates both values
   */
  const onPresetSelect = useCallback(
    (preset: DateRangePreset) => {
      const dateRange = getDateRangeFromPreset(preset);
      setDates(dateRange);
      setSelectedPreset(preset);
    },
    [setDates, setSelectedPreset]
  );

  return {
    dates,
    selectedPreset,
    onChange,
    onPresetSelect,
  };
}
